// H2 Farm — Commercial pricing engine (Phase 2 · B.2)
// Pure helpers. NO side-effects. Reusable by every lot (Saffron, Algae, L3/L4/L5).
//
// Governance: PRODUZIONE ≠ PREZZO ≠ RICAVO
// The functions below operate on `channel` and `pricing` objects only.
// They never mutate production, yield, OPEX, CAPEX or physical parameters.

export const PRICE_STATUS = Object.freeze({
  CONTRACT: "CONTRACT",
  USER_OVERRIDE: "USER_OVERRIDE",
  VERIFIED_MARKET: "VERIFIED_MARKET",
  SOURCE_PROVIDED: "SOURCE_PROVIDED",
  BENCHMARK: "BENCHMARK",
  PROJECT_ESTIMATE: "PROJECT_ESTIMATE",
  ASSUMPTION: "ASSUMPTION",
  TO_VALIDATE: "TO_VALIDATE",
});

export const COMMERCIAL_TIER = Object.freeze({
  HIGH_VALUE_ACTIVE: "HIGH_VALUE_ACTIVE",
  STANDARDIZED_EXTRACT: "STANDARDIZED_EXTRACT",
  PREMIUM_DRIED: "PREMIUM_DRIED",
  FALLBACK_SECONDARY: "FALLBACK_SECONDARY",
});

export const TARGET_MARKET = Object.freeze({
  Pharma: "Pharma",
  Nutraceutical: "Nutraceutical",
  Cosmetics: "Cosmetics",
  Ingredient_Supplier: "Ingredient_Supplier",
  Premium_Food: "Premium_Food",
  Biotech: "Biotech",
  Research: "Research",
  Other: "Other",
});

export const QUALITY_GRADE = Object.freeze({
  GMP: "GMP",
  Food_grade: "Food-grade",
  Cosmetic_grade: "Cosmetic-grade",
  Technical: "Technical",
  Custom: "Custom",
});

export const SCENARIOS = Object.freeze(["conservative", "base", "premium", "custom"]);

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// Contract price gate: valid iff enabled AND current_year in [year_from, year_to]
export const contractPriceActive = (channel, currentYear) => {
  const c = channel?.pricing?.contract;
  if (!c || !c.enabled) return false;
  if (typeof c.price_eur_kg !== "number" || c.price_eur_kg <= 0) return false;
  const y = Number(currentYear) || 0;
  const yf = Number(c.year_from) || 0;
  const yt = Number(c.year_to) || 0;
  if (yf > 0 && y < yf) return false;
  if (yt > 0 && y > yt) return false;
  return true;
};

// Resolve the price actually applied to revenue, with 4-layer precedence:
//   1. CONTRACT      (if active in current year)
//   2. USER_OVERRIDE (if value is provided)
//   3. SCENARIO      (base/conservative/premium/custom -> range or benchmark_base)
//   4. Fallback      (benchmark_base.value)
export const resolveEffectivePrice = (channel, scenario = "base", currentYear = 1) => {
  const pricing = channel?.pricing || {};

  // 1. CONTRACT (highest priority)
  if (contractPriceActive(channel, currentYear)) {
    return {
      value: pricing.contract.price_eur_kg,
      status: PRICE_STATUS.CONTRACT,
      source: "contract",
    };
  }

  // 2. USER OVERRIDE
  const uo = pricing.user_override;
  if (uo && typeof uo.value === "number" && uo.value > 0) {
    return { value: uo.value, status: PRICE_STATUS.USER_OVERRIDE, source: "user_override" };
  }

  // 3. SCENARIO
  const bb = pricing.benchmark_base;
  const range = pricing.range;
  switch (scenario) {
    case "conservative":
      if (range && typeof range.min === "number") {
        return { value: range.min, status: range.classification || bb?.status || PRICE_STATUS.ASSUMPTION, source: "range.min" };
      }
      break;
    case "premium":
      if (range && typeof range.max === "number") {
        return { value: range.max, status: range.classification || bb?.status || PRICE_STATUS.ASSUMPTION, source: "range.max" };
      }
      break;
    case "custom":
      if (uo && typeof uo.value === "number" && uo.value > 0) {
        return { value: uo.value, status: PRICE_STATUS.USER_OVERRIDE, source: "custom" };
      }
      break;
    case "base":
    default:
      break;
  }

  // 4. Fallback: benchmark_base
  if (bb && typeof bb.value === "number") {
    return { value: bb.value, status: bb.status || PRICE_STATUS.ASSUMPTION, source: "benchmark_base" };
  }
  return { value: 0, status: PRICE_STATUS.TO_VALIDATE, source: "missing" };
};

// Weighted average price €/kg over final_kg of every channel that contributes revenue.
export const computeWeightedAvgPrice = (channelsResults = []) => {
  const totalKg = channelsResults.reduce((s, c) => s + (Number(c.final_kg) || 0), 0);
  const totalRev = channelsResults.reduce((s, c) => s + (Number(c.revenue) || 0), 0);
  return totalKg > 0 ? totalRev / totalKg : 0;
};

// Conservation-of-mass hard-guard on allocation percentages.
// Returns { ok, sum, deviation }. tolerance is ±0.5% by default.
export const guardConservationOfMass = (channels = [], tolerance = 0.5) => {
  const sum = channels.reduce((s, ch) => s + (Number(ch?.allocation_pct) || 0), 0);
  const deviation = sum - 100;
  return { ok: Math.abs(deviation) <= tolerance, sum, deviation };
};

// Yield clamp with warning flag.
export const clampYieldOperating = (yieldRange = {}) => {
  const min = Number(yieldRange.min) || 0;
  const max = Number(yieldRange.max) || 100;
  const base = Number(yieldRange.base) || min;
  const requested = Number(yieldRange.operating);
  const value = Number.isFinite(requested) ? clamp(requested, min, max) : base;
  return {
    value,
    clamped: Number.isFinite(requested) && (requested < min || requested > max),
  };
};

// Compute revenue for a single channel from raw material. Pure function.
// raw_material_kg    : kg materia prima disponibile a monte del canale
// scenario           : "conservative" | "base" | "premium" | "custom"
// current_year       : intero (1..N) per attivare CONTRACT layer
// Returns per-channel breakdown.
export const computeChannelRevenue = (channel, raw_material_kg, scenario = "base", currentYear = 1) => {
  const alloc = Number(channel?.allocation_pct) || 0;
  const raw_allocated = (Number(raw_material_kg) || 0) * alloc / 100;
  const yc = clampYieldOperating(channel?.transformation?.yield_range || {});
  const final_kg = channel?.is_intermediate ? 0 : raw_allocated * yc.value / 100;
  const eff = resolveEffectivePrice(channel, scenario, currentYear);
  const revenue = final_kg * eff.value;
  return {
    key: channel?.key,
    name: channel?.name,
    allocation_pct: alloc,
    raw_allocated,
    yield_operating: yc.value,
    yield_clamped: yc.clamped,
    final_kg,
    effective_price: eff.value,
    price_status: eff.status,
    price_source: eff.source,
    revenue,
    is_intermediate: !!channel?.is_intermediate,
    commercial_tier: channel?.commercial_tier,
    target_market: channel?.target_market,
  };
};

// Aggregate revenue over the channels of a lot.
// - Enforces conservation-of-mass (Σ allocation_pct == 100 ± tolerance).
// - Excludes is_intermediate channels from total_revenue.
export const aggregateChannels = (channels = [], raw_material_kg, scenario = "base", currentYear = 1) => {
  const guard = guardConservationOfMass(channels);
  const results = channels.map((ch) => computeChannelRevenue(ch, raw_material_kg, scenario, currentYear));
  const total_final_kg = results.reduce((s, r) => s + (r.is_intermediate ? 0 : r.final_kg), 0);
  const total_revenue = results.reduce((s, r) => s + (r.is_intermediate ? 0 : r.revenue), 0);
  const weighted_avg_price = computeWeightedAvgPrice(results.filter((r) => !r.is_intermediate));
  return {
    channels: results,
    total_final_kg,
    total_revenue,
    weighted_avg_price,
    allocation_sum_pct: guard.sum,
    conservation_of_mass_ok: guard.ok,
    scenario,
  };
};
